import { FiCalendar, FiCreditCard } from "react-icons/fi";
import { PaymentDetailsInterface } from "./PaymentDetailsInterface";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";
import { formatDateInZone, formatCurrency, getCurrencySymbol } from "../../utils/formatters";

export const PaymentDetailsCard = ({ paymentDetails }: { paymentDetails: PaymentDetailsInterface }) => {
  const { language, timezone, country } = useAuth();
  const displayCurrency = paymentDetails.currencyCode || paymentDetails.countryCode || country;
  const currencySymbol = getCurrencySymbol(displayCurrency);
  const currencySuffix = paymentDetails.currencyCode || "";

  const benefitTotal = paymentDetails.benefitPurchases?.reduce((sum, b) => sum + (b.price || 0), 0) ?? 0;
  const productsSubtotal = paymentDetails.products?.reduce((sum, p) => sum + p.total_value_with_discount, 0) ?? paymentDetails.subtotal;
  const displayTotal = paymentDetails.products && paymentDetails.products.length > 0
    ? productsSubtotal + benefitTotal
    : paymentDetails.total;
  const isPaid = paymentDetails.paymentState === "paid";
  const paymentMethodLabel = paymentDetails.paymentMethod === "cod"
    ? dictionary["counter-delivery"][language]
    : paymentDetails.paymentMethod;
  const formatPaymentAmount = (amount: number) =>
    `${formatCurrency(amount, currencySymbol)}${currencySuffix ? ` ${currencySuffix}` : ""}`;

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <h2 className="text-lg font-semibold mb-4">{dictionary["payment-details"][language]}</h2>

      <div className="space-y-3">
        {/* Product breakdown */}
        {paymentDetails.products && paymentDetails.products.length > 0 && (
          <div className="space-y-1">
            {paymentDetails.products.map((product) => (
              <div key={product.id} className="flex justify-between text-sm">
                <span className="text-gray-600 truncate mr-2">
                  {product.name} ×{product.units}
                </span>
                <span className="text-gray-700 whitespace-nowrap">
                  {formatPaymentAmount(product.total_value_with_discount)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Benefit purchases */}
        {paymentDetails.benefitPurchases && paymentDetails.benefitPurchases.length > 0 && (
          <div className="space-y-1">
            {paymentDetails.benefitPurchases.map((benefit, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-purple-600 truncate mr-2">
                  🎁 {benefit.name || benefit.label}
                </span>
                <span className="text-gray-700 whitespace-nowrap">
                  {benefit.price > 0 ? formatPaymentAmount(benefit.price) : (dictionary["free"]?.[language] ?? "Gratis")}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Subtotal */}
        <div className="flex justify-between pt-2 border-t border-gray-100">
          <span className="text-gray-600">{dictionary["subtotal"][language]}</span>
          <span className="font-medium">{formatPaymentAmount(productsSubtotal)}</span>
        </div>

        {/* Benefit total if any */}
        {benefitTotal > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">{dictionary["additional-offer"]?.[language] ?? "Oferta adicional"}</span>
            <span className="font-medium">{formatPaymentAmount(benefitTotal)}</span>
          </div>
        )}

        {/* Total */}
        <div className="flex justify-between pt-3 border-t border-gray-200">
          <span className="font-medium">{dictionary["total"][language]}</span>
          <span className="font-semibold">{formatPaymentAmount(displayTotal)}</span>
        </div>

        {/* Payment method */}
        {paymentDetails?.paymentMethod && (
        <div className="flex items-center gap-2 pt-3 text-sm text-gray-600">
          <FiCreditCard className="text-gray-400" />
          <span>
            {isPaid ? dictionary["paid-with"][language] : dictionary["payment-method"][language]} {paymentMethodLabel}
          </span>
          </div>
        )}

        {/* Payment date */}
        {isPaid && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <FiCalendar className="text-gray-400" />
            <span>{dictionary["paid-on"][language]} {formatDateInZone(paymentDetails.paymentDate, timezone)}</span>
          </div>
        )}
      </div>
    </div>
  );
};
